/**
 * Meeting Conclude Component
 * 
 * Features:
 * - AI-generated meeting recap
 * - Each attendee rates the meeting (1-10)
 * - Cascading message
 * - Final rating display
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import UserAvatar from './UserAvatar';
import { Sparkles, Loader2, Star, MessageSquare, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MeetingConclude = ({ meetingId, attendees = [], sectionData = {} }) => {
  const [recap, setRecap] = useState('');
  const [cascadingMessage, setCascadingMessage] = useState('');
  const [isGeneratingRecap, setIsGeneratingRecap] = useState(false);
  const [attendeeRatings, setAttendeeRatings] = useState({});
  const [currentRatingUser, setCurrentRatingUser] = useState(0);
  
  // Present attendees only
  const presentAttendees = attendees.filter(a => a.status === 'present');
  const currentAttendee = presentAttendees[currentRatingUser];
  
  // Calculate average rating
  const ratings = Object.values(attendeeRatings).filter(r => r > 0);
  const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : null;
  
  // Check if all attendees have rated
  const allRated = presentAttendees.length > 0 && presentAttendees.every(a => attendeeRatings[a.user_id] > 0);

  const generateRecap = async () => {
    setIsGeneratingRecap(true);
    try {
      // Build context from section data
      const context = {
        scorecard_summary: sectionData.scorecard?.summary || {},
        rocks_count: sectionData.rocks?.length || 0,
        headlines_count: sectionData.headlines?.length || 0,
        todos_open: sectionData.todos?.filter(t => t.status !== 'complete').length || 0,
        todos_complete: sectionData.todos?.filter(t => t.status === 'complete').length || 0,
        issues_count: sectionData.issues?.filter(i => i.status !== 'solved').length || 0,
        attendees_count: presentAttendees.length
      };
      
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/generate-recap`, {
        context: JSON.stringify(context)
      });
      
      setRecap(response.data.recap || 'Meeting completed successfully.');
      toast.success('Recap generated!');
    } catch (error) {
      console.error('Error generating recap:', error);
      // Generate a simple recap locally if API fails
      const localRecap = `Meeting concluded with ${presentAttendees.length} attendees. ` +
        `Reviewed ${sectionData.scorecard?.metrics?.length || 0} scorecard metrics, ` +
        `${sectionData.rocks?.length || 0} rocks, and ${sectionData.issues?.length || 0} issues.`;
      setRecap(localRecap);
      toast.info('Generated basic recap');
    } finally {
      setIsGeneratingRecap(false);
    }
  };

  const handleRating = async (userId, rating) => {
    // Update local state immediately for UI responsiveness
    setAttendeeRatings(prev => ({
      ...prev,
      [userId]: rating
    }));
    
    // FIX 3: Save rating immediately to backend
    try {
      await axios.patch(`${API}/eos/meetings/${meetingId}/rating`, {
        rating: rating
      });
      toast.success(`Rating saved: ${rating}/10`);
    } catch (error) {
      console.error('Error saving rating:', error);
      // Don't show error toast - rating is still saved locally
    }
    
    // Move to next attendee if not the last one
    if (currentRatingUser < presentAttendees.length - 1) {
      setTimeout(() => {
        setCurrentRatingUser(currentRatingUser + 1);
      }, 500);
    }
  };

  const saveMeetingConclusion = async () => {
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/conclude`, {
        recap,
        cascading_message: cascadingMessage,
        ratings: attendeeRatings,
        average_rating: avgRating
      });
      toast.success('Meeting conclusion saved!');
    } catch (error) {
      console.error('Error saving conclusion:', error);
      toast.error('Failed to save conclusion');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Conclude Meeting</h3>
        {avgRating && (
          <Badge className="bg-cyan-500 text-white">
            <Star className="h-3 w-3 mr-1 fill-current" />
            Avg Rating: {avgRating}/10
          </Badge>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Meeting Recap - AI Assisted */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Meeting Recap</Label>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={generateRecap}
              disabled={isGeneratingRecap}
              className="text-xs h-7"
            >
              {isGeneratingRecap ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="h-3 w-3 mr-1" /> AI Generate</>
              )}
            </Button>
          </div>
          <Textarea 
            placeholder="Key takeaways from today's meeting..." 
            rows={4}
            value={recap}
            onChange={(e) => setRecap(e.target.value)}
            className="text-sm"
          />
        </div>
        
        {/* Cascading Message */}
        <div className="space-y-2">
          <Label className="text-sm">Cascading Message</Label>
          <Textarea 
            placeholder="Message to share with the team..." 
            rows={4}
            value={cascadingMessage}
            onChange={(e) => setCascadingMessage(e.target.value)}
            className="text-sm"
          />
          <p className="text-[10px] text-slate-500">This will be shared with team members who weren&apos;t in attendance.</p>
        </div>
      </div>
      
      {/* Individual Ratings Section */}
      <Card className="bg-slate-900/50 border-slate-700">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-cyan-500" />
            <h4 className="font-medium text-sm">Team Meeting Ratings</h4>
            <span className="text-xs text-slate-500">
              ({Object.values(attendeeRatings).filter(r => r > 0).length}/{presentAttendees.length} rated)
            </span>
          </div>
          
          {presentAttendees.length > 0 ? (
            <div className="space-y-3">
              {/* Current Attendee Rating */}
              {currentAttendee && !attendeeRatings[currentAttendee.user_id] && (
                <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <UserAvatar user={currentAttendee} size="sm" />
                    <div>
                      <p className="font-medium text-sm">{currentAttendee.display_name}</p>
                      <p className="text-xs text-cyan-400">Rate this meeting (1-10)</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                      <Button 
                        key={r} 
                        variant="outline" 
                        size="sm" 
                        className={`w-8 h-8 p-0 text-xs ${
                          r <= 3 ? 'hover:bg-red-500/20 hover:border-red-500' :
                          r <= 6 ? 'hover:bg-yellow-500/20 hover:border-yellow-500' :
                          'hover:bg-green-500/20 hover:border-green-500'
                        }`}
                        onClick={() => handleRating(currentAttendee.user_id, r)}
                      >
                        {r}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* All Attendees Ratings */}
              <ScrollArea className="h-32">
                <div className="space-y-2">
                  {presentAttendees.map((attendee, idx) => {
                    const rating = attendeeRatings[attendee.user_id];
                    const isCurrentlyRating = idx === currentRatingUser && !rating;
                    
                    return (
                      <div 
                        key={attendee.user_id}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          isCurrentlyRating ? 'bg-cyan-500/10 border border-cyan-500/30' :
                          rating ? 'bg-slate-800/50' : 'bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <UserAvatar user={attendee} size="xs" />
                          <span className="text-xs">{attendee.display_name?.split(' ')[0]}</span>
                        </div>
                        {rating ? (
                          <Badge 
                            className={`text-xs ${
                              rating <= 3 ? 'bg-red-500' :
                              rating <= 6 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                          >
                            {rating}/10
                          </Badge>
                        ) : isCurrentlyRating ? (
                          <span className="text-[10px] text-cyan-400 animate-pulse">Rating...</span>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-6 px-2"
                            onClick={() => setCurrentRatingUser(idx)}
                          >
                            Rate
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <p className="text-center text-slate-500 text-sm py-4">No attendees to rate</p>
          )}
        </CardContent>
      </Card>
      
      {/* Summary */}
      {allRated && (
        <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 text-center">
          <p className="text-sm text-green-400">
            <Star className="h-4 w-4 inline mr-1 fill-current" />
            All attendees rated! Average: <strong>{avgRating}/10</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default MeetingConclude;
